"use client";

import { convertWithBr } from "@/lib/helper/converter";
import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import hasFadeAnim from "@/lib/animation/hasFadeAnim";
import Image from "next/image";
import hasCharAnim from "@/lib/animation/hasCharAnim";
import hasTextMovAnim from "@/lib/animation/hasTextMovAnim";

type Props = {
  title: string;
  intro_text: string;
  contact_title: string;
  contact_links: {
    key: string;
    name: string;
    value: string;
  }[];
  office: {
    title: string;
    address: string;
  };
  contact_footer_title: string;
  contact_footer_desc: string;
};

const ContactMain = ({
  title,
  intro_text,
  contact_title,
  contact_links,
  office,
  contact_footer_title,
  contact_footer_desc,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null!);

  useGSAP(
    () => {
      hasFadeAnim();
      hasCharAnim();
      hasTextMovAnim();
    },
    { scope: containerRef },
  );

  return (
    <section
      className="w-full bg-white text-black section-spacing-top section-spacing-bottom"
      ref={containerRef}
    >
      <div className="inner-container-2">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-stretch">
          {/* Left Column — Large Title */}
          <div className="lg:col-span-7 flex flex-col justify-start">
            <h1
              className="text-[42px] sm:text-[54px] md:text-[68px] lg:text-[80px] xl:text-[95px] 2xl:text-[110px] font-light leading-[1.1] tracking-snug text-black has_char_anim md:mt-[-10px]"
              data-delay="0.10"
              data-duration="0.80"
              data-stagger="0.015"
              dangerouslySetInnerHTML={convertWithBr(title)}
            />
          </div>

          {/* Right Column — Info */}
          <div className="lg:col-span-5 flex flex-col gap-8 lg:gap-14 has_fade_anim">
            {/* Intro */}
            <p className="text-[18px] sm:text-[20px] lg:text-[22px] leading-snug text-black/90 font-light max-w-[500px]">
              {intro_text}
            </p>

            {/* Get In Touch Section */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <h3 className="text-[20px] md:text-[22px] lg:text-[24px] leading-snug font-bold text-black w-full sm:w-fit pr-10">
                  {contact_title}
                </h3>
              </div>

              {/* Contact Links */}
              {contact_links.length > 0 && (
                <div className="flex flex-col gap-2">
                  {contact_links.map((link, index) => (
                    <div key={index} className="flex flex-row items-center gap-2">
                      <Image
                        src={`/assets/imgs/logo/${link.key}.png`}
                        alt={link.name}
                        width={25}
                        height={25}
                        className="object-contain w-[25px] h-[25px]"
                      />
                      <div className="text-[14px] md:text-[16px] lg:text-[18px] leading-snug">
                        <a
                          href={
                            link.key === "email"
                              ? `mailto:${link.value}`
                              : `https://wa.me/${link.value}`
                          }
                          className="text-black/60 hover:text-black transition-colors underline underline-offset-4 decoration-black/20 hover:decoration-black"
                        >
                          {link.name}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Office */}
              {office?.title && (
                <div className="flex flex-col text-[14px] md:text-[16px] lg:text-[18px] text-black/90 leading-snug font-light">
                  <p className="font-medium text-[14px] md:text-[16px] lg:text-[18px]">
                    {office?.title}
                  </p>
                  <p className="max-w-[420px] text-[14px] md:text-[16px] lg:text-[18px]">
                    {office?.address}
                  </p>
                </div>
              )}
            </div>

            {/* Work With Us Section */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <h3 className="text-[20px] md:text-[22px] lg:text-[24px] leading-snug font-bold text-black w-full sm:w-fit pr-10">
                  {contact_footer_title}
                </h3>
              </div>
              <div className="flex flex-col text-[14px] md:text-[16px] lg:text-[18px] text-black/90 leading-snug font-light">
                <p
                  className="text-[14px] md:text-[16px] lg:text-[18px] leading-snug text-black/90 font-light max-w-[500px]"
                  dangerouslySetInnerHTML={convertWithBr(contact_footer_desc)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactMain;
